/**
 * Role display names ordered by priority (highest first).
 * When a user has multiple groups, the highest-priority match is shown.
 */
export const ROLE_DISPLAY: { key: string; label: string }[] = [
    { key: 'admin', label: 'Admin' },
    { key: 'super-author', label: 'Super Author' },
    { key: 'oim', label: 'OIM' },
    { key: 'odp-igi-bi', label: 'ODP/IGI/BI' },
    { key: 'csir-sup', label: 'CSIR Supervisor' },
    { key: 'alarms-sup', label: 'Alarms Supervisor' },
    { key: 'cs-sup', label: 'CS Supervisor' },
    { key: 'events-sup', label: 'Events Supervisor' },
    { key: 'cm-sup', label: 'CM Supervisor' },
    { key: 'csir', label: 'CSIR' },
    { key: 'alarms', label: 'Alarms' },
    { key: 'cs', label: 'CS' },
    { key: 'events', label: 'Events' },
    { key: 'cm', label: 'CM' }
];

/**
 * Given a user's permissions array, return the highest-priority display role.
 */
export function getDisplayRole(permissions: string[]): string {
    const lowerPerms = permissions.map(p => p.toLowerCase());
    for (const role of ROLE_DISPLAY) {
        if (lowerPerms.includes(role.key)) return role.label;
    }
    return 'Read-Only';
}

/**
 * Maps supervisor roles to their default visibility (service line value).
 * Used to pre-select the Visibility dropdown for supervisor users.
 */
export const SUPERVISOR_DEFAULT_VISIBILITY: Record<string, string[]> = {
    'alarms-sup': ['alarms'],
    'cm-sup': ['cm'],
    'cs-sup': ['cs'],
    'csir-sup': ['csir'],
    'events-sup': ['events'],
};

/**
 * Maps supervisor roles to all visibility options they are allowed to select.
 * This is a superset of SUPERVISOR_DEFAULT_VISIBILITY — includes extra groups
 * the supervisor can optionally target but aren't pre-selected.
 */
export const SUPERVISOR_ALLOWED_VISIBILITY: Record<string, string[]> = {
    'alarms-sup': ['alarms'],
    'cm-sup': ['cm', 'csir'],
    'cs-sup': ['cs'],
    'csir-sup': ['csir'],
    'events-sup': ['events'],
};

/**
 * Given a user's permissions, return the default visibility values for the dropdown.
 * Supervisors get their service line pre-selected; other roles get no default.
 */
export function getDefaultVisibility(permissions: string[]): string[] {
    const lowerPerms = permissions.map(p => p.toLowerCase());
    for (const [role, values] of Object.entries(SUPERVISOR_DEFAULT_VISIBILITY)) {
        if (lowerPerms.includes(role)) return values;
    }
    return [];
}

/**
 * Given a user's permissions, return all visibility options they are allowed to select.
 * Used to determine which dropdown options are enabled vs disabled.
 */
export function getAllowedVisibility(permissions: string[]): string[] {
    const lowerPerms = permissions.map(p => p.toLowerCase());
    for (const [role, values] of Object.entries(SUPERVISOR_ALLOWED_VISIBILITY)) {
        if (lowerPerms.includes(role)) return values;
    }
    return [];
}

// ---- Passdown tab read access -------------------------------------

/** All Passdowns tab ids, in display order. */
export const PASSDOWN_TABS = ['global', 'alarms', 'cs', 'csir', 'cm', 'events', 'oim'] as const;

/** Roles that can read every passdown tab. */
const PASSDOWN_ELEVATED_ROLES = ['admin', 'oim', 'odp-igi-bi', 'super-author'];

/**
 * Service line supervisor roles. In addition to their own line, these roles get
 * read-only access to the OIM passdown tab (backend grants read but not write).
 */
const SUPERVISOR_ROLES = Object.keys(SUPERVISOR_DEFAULT_VISIBILITY);

/**
 * Maps a user's role (supervisor or service-line member) to its service line tab.
 */
const ROLE_TO_PASSDOWN_LINE: Record<string, string> = {
    'alarms-sup': 'alarms', 'alarms': 'alarms',
    'cm-sup': 'cm', 'cm': 'cm',
    'cs-sup': 'cs', 'cs': 'cs',
    'csir-sup': 'csir', 'csir': 'csir',
    'events-sup': 'events', 'events': 'events',
};

/**
 * Service lines that do NOT also get access to the Global passdown tab.
 * Per requirement: all supervisors EXCEPT CM see Global + their own line.
 */
const LINES_WITHOUT_GLOBAL = ['cm'];

/**
 * Given a user's permissions, return the passdown tab ids they are allowed to read.
 * - Admin / OIM / Super Author: all tabs.
 * - Supervisors and service-line members: their own line, plus Global unless the
 *   line is excluded (CM).
 * - Service line supervisors additionally get the OIM tab (read-only).
 */
export function getReadablePassdownTabs(permissions: string[]): string[] {
    const lowerPerms = permissions.map(pms => pms.toLowerCase());

    if (lowerPerms.some(pms => PASSDOWN_ELEVATED_ROLES.includes(pms))) {
        return [...PASSDOWN_TABS];
    }

    const tabs = new Set<string>();
    for (const [role, line] of Object.entries(ROLE_TO_PASSDOWN_LINE)) {
        if (lowerPerms.includes(role)) {
            tabs.add(line);
            if (!LINES_WITHOUT_GLOBAL.includes(line)) {
                tabs.add('global');
            }
        }
    }

    // Service line supervisors get read-only visibility into the OIM passdown.
    // The backend grants them read (not write) on oimShiftPassdown, so the tab
    // renders without edit controls.
    if (lowerPerms.some(pms => SUPERVISOR_ROLES.includes(pms))) {
        tabs.add('oim');
    }

    return PASSDOWN_TABS.filter(tb => tabs.has(tb));
}